import type { ComponentProps } from 'react';
import React, { useEffect, useRef } from 'react';
import Copy from '@uber/base-icons/copy';
import Pencil from '@uber/base-icons/pencil';
import TrashCan from '@uber/base-icons/trash-can';
import type { Theme } from 'baseui';
import { styled } from 'baseui';
import { Button, KIND as BTN_KIND, SHAPE, SIZE } from 'baseui/button';
import { Checkbox } from 'baseui/checkbox';
import { toaster } from 'baseui/toast';
import { ACTION_BY, LOCK_ANNOTATION_ACTION, VISIBILITY_ANNOTATION_ACTION } from '../constant/constant';
import type { GenericAnnotationT, GetAnnotationMetaDetailsT } from '../types/types';
import type { DeleteAnnotationPayloadT, SelectAnnotationPayloadT, ToggleLockAnnotationPayloadT, ToggleVisibilityAnnotationPayloadT } from './analytics/types';
import useAnalyticsForSidePanel from './analytics/useAnalyticsForSidePanel';
import { ActionItem, ANNOTATION_ITEM_ATTRIBUTE_ID, DELETE_BUTTON_SELECTOR_ATTRIBUTE_ID, HideButton, LOCK_BUTTON_SELECTOR_ATTRIBUTE_ID, LockButton, SHOW_HIDE_BUTTON_SELECTOR_ATTRIBUTE_ID } from './annotation-item';
import { ACTION_BUTTON_SIZE, ACTION_TYPE, TOAST_MESSAGE } from './constants';
import { SWATCH_CIRCLE_SIZE } from './styled-components';
export const getBackgroundColor = ({
  colors,
  locked
}) => {
  if (locked) {
    return colors.contentInversePrimary;
  }
  return colors.backgroundPrimary;
};
const deleteButtonOverride = {
  BaseButton: {
    style: ({
      $theme
    }: {
      $theme: Theme;
    }) => ({
      color: $theme.colors.negative
    })
  }
};
const IconContainer = styled('div', {
  borderRadius: '4px',
  backgroundColor: 'inherit',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '25px',
  height: '25px'
});
const AnnotationContent = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  flexGrow: '1',
  paddingLeft: `${SWATCH_CIRCLE_SIZE}px`
});
const HIDDEN_TINT_OFFSET = '4px';
const HiddenTint = styled('div', ({
  $theme: {
    colors
  }
}) => ({
  position: 'absolute',
  backgroundColor: colors.backgroundPrimary,
  opacity: '40%',
  height: '100%',
  width: `calc(100% + ${HIDDEN_TINT_OFFSET})`,
  top: '0px',
  left: `-${HIDDEN_TINT_OFFSET}`,
  pointerEvents: 'none'
}));
const LockedTint = styled('div', ({
  $theme: {
    colors
  }
}) => ({
  position: 'absolute',
  backgroundColor: colors.contentInversePrimary,
  opacity: '30%',
  height: '100%',
  width: `calc(100% + ${HIDDEN_TINT_OFFSET})`,
  top: '0px',
  left: `-${HIDDEN_TINT_OFFSET}`,
  pointerEvents: 'none'
}));
const AnnotationKey = styled('div', ({
  $theme: {
    colors,
    sizing
  }
}) => ({
  fontSize: sizing.scale500,
  color: `${colors.contentSecondary}`,
  width: '100px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
}));
const ItemContainer = styled<'div', {
  $isEditable: boolean;
  $isDisabled?: boolean;
  $isSelected: boolean;
  $isFocused: boolean;
  $isLocked?: boolean;
}>('div', ({
  $theme: {
    colors,
    sizing,
    borders
  },
  $isEditable,
  $isDisabled,
  $isLocked,
  $isSelected
}) => ({
  backgroundColor: getBackgroundColor({
    locked: $isLocked,
    colors
  }),
  border: $isSelected ? `2px solid ${colors.borderSelected}` : 'none',
  borderRadius: borders.radius200,
  borderLeft: `${sizing.scale100} solid ${$isEditable ? colors.accent : 'transparent'}`,
  color: colors.contentPrimary,
  transition: 'background-color 0.2s, border 0.2s, max-height 0.5s',
  padding: '8px 12px 8px 4px',
  ...($isDisabled && {
    opacity: 0.5,
    pointerEvents: 'none'
  })
}));
const AnnotationDetailsContainer = styled('div', {
  display: 'flex',
  alignItems: 'center',
  position: 'relative'
});
type PropsT = {
  annotation: GenericAnnotationT;
  id: string;
  onAnnotationSelect: (a: string) => void;
  onAnnotationHover: (a: string | null) => void;
  onAnnotationLockToggle?: (b: boolean, a: string) => void;
  onAnnotationHideToggle: (b: boolean, a: string) => void;
  onAnnotationCheckedToggle?: (annotationId: string, isChecked: boolean) => void;
  onAnnotationDelete?: (a: string) => void;
  onAnnotationEdit?: (a: string) => void;
  onCopyAnnotationId?: (a: string | null) => void;
  isEditable: boolean;
  isSelected: boolean;
  isFocused: boolean;
  isLocked?: boolean;
  getAnnotationMetaDetails: GetAnnotationMetaDetailsT;
  isCheckboxDisabled?: boolean;
  isChecked?: boolean;
};
const AnnotationSubItem = ({
  annotation,
  id,
  onAnnotationSelect,
  onAnnotationHover,
  onAnnotationLockToggle,
  onAnnotationHideToggle,
  onAnnotationCheckedToggle,
  onAnnotationDelete,
  onAnnotationEdit,
  onCopyAnnotationId,
  isEditable,
  isSelected,
  isFocused,
  isLocked,
  getAnnotationMetaDetails,
  isCheckboxDisabled,
  isChecked
}: PropsT) => {
  const ref = useRef(null);
  const {
    sendEventForAnnotationSelection,
    sendEventForToggleVisibilityAnnotation,
    sendEventForToggleLockAnnotation,
    sendEventForDeleteAnnotation
  } = useAnalyticsForSidePanel();
  const analyticsForLockToggle = (locked: boolean) => {
    const eventPayload: ToggleLockAnnotationPayloadT = {
      objectType: annotation.annotationType,
      label: annotation.annotationLabel.title,
      objectId: id,
      action: locked ? LOCK_ANNOTATION_ACTION.LOCK : LOCK_ANNOTATION_ACTION.UNLOCK
    };
    sendEventForToggleLockAnnotation(eventPayload);
  };
  const analyticsForVisibilityToggle = (hidden: boolean) => {
    const eventPayload: ToggleVisibilityAnnotationPayloadT = {
      objectType: annotation.annotationType,
      label: annotation.annotationLabel.title,
      objectId: id,
      action: hidden ? VISIBILITY_ANNOTATION_ACTION.HIDE : VISIBILITY_ANNOTATION_ACTION.SHOW
    };
    sendEventForToggleVisibilityAnnotation(eventPayload);
  };
  const handleToggleAnnotationVisibility = (e, isVisible: boolean) => {
    e.stopPropagation();
    onAnnotationHideToggle(isVisible, id);
    analyticsForVisibilityToggle(isVisible);
  };
  const handleToggleAnnotationLock = (e, isLocked: boolean) => {
    if (!onAnnotationLockToggle) {
      return null;
    }
    e.stopPropagation();
    onAnnotationLockToggle(isLocked, id);
    analyticsForLockToggle(isLocked);
  };
  useEffect(() => {
    if (isEditable) {
      // @ts-expect-error missing null check
      ref.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [isEditable]);
  const handleAnnotationSelect = () => {
    if (isLocked) {
      return;
    }
    const eventPayload: SelectAnnotationPayloadT = {
      objectType: annotation.annotationType,
      label: annotation.annotationLabel.title,
      objectId: id,
      actionBy: ACTION_BY.EDIT
    };
    sendEventForAnnotationSelection(eventPayload);
    onAnnotationSelect(id);
  };
  const handleAnnotationDelete = onAnnotationDelete && (e => {
    e.stopPropagation();
    const eventPayload: DeleteAnnotationPayloadT = {
      objectType: annotation.annotationType,
      label: annotation.annotationLabel.title,
      objectId: id,
      actionBy: ACTION_BY.EDIT
    };
    sendEventForDeleteAnnotation(eventPayload);
    onAnnotationDelete(id);
  });
  const handleAnnotationEdit = onAnnotationEdit && (e => {
    e.stopPropagation();
    onAnnotationEdit(id);
  });
  const handleCopyAnnotationId = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onCopyAnnotationId?.(id);
    toaster.show(TOAST_MESSAGE.SUCCESSFULLY_COPIED_ANNOTATION, {
      autoHideDuration: 1500
    });
  };
  const handleAnnotationCheckedToggle = onAnnotationCheckedToggle && (e => {
    e.stopPropagation();
    onAnnotationCheckedToggle(id, e.target.checked);
  });
  const metadata = getAnnotationMetaDetails({
    annotationId: id
  });
  const {
    config
  } = metadata || {};
  const itemContainerProps: ComponentProps<typeof ItemContainer> = {
    ref,
    'data-testid': `annotation-item-${id}`,
    'data-selector-id': ANNOTATION_ITEM_ATTRIBUTE_ID,
    'data-annotation-editable': isEditable,
    'data-annotation-selected': isSelected,
    onClick: e => {
      e.stopPropagation();
      handleAnnotationSelect();
    },
    onMouseEnter: e => {
      e.stopPropagation();
      if (annotation.hidden) return;
      onAnnotationHover(id);
    },
    onMouseLeave: e => {
      e.stopPropagation();
      if (annotation.hidden) return;
      onAnnotationHover(null);
    },
    $isSelected: isSelected,
    $isEditable: isEditable,
    $isFocused: isFocused,
    $isLocked: isLocked,
    $isDisabled: config?.disableSelect
  };
  if (metadata && 'parent' in metadata) {
    return <ItemContainer {...itemContainerProps}>{metadata.parent}</ItemContainer>;
  }
  const {
    icon,
    description
  } = metadata || {};
  return <ItemContainer {...itemContainerProps}>
      <AnnotationDetailsContainer>
        <IconContainer>{icon}</IconContainer>
        {annotation.hidden && <HiddenTint />}
        {isLocked && <LockedTint />}
        <AnnotationContent>
          {annotation.annotationLabel.title} <AnnotationKey>ID: {id}</AnnotationKey>
          {description}
        </AnnotationContent>
        <ActionItem>
          {onCopyAnnotationId && <Button size={SIZE.mini} onClick={handleCopyAnnotationId} shape={SHAPE.circle} kind={BTN_KIND.tertiary}>
              <Copy title={ACTION_TYPE.COPY_ANNOTATION_ID} width={ACTION_BUTTON_SIZE.COPY_ANNOTATION_ID} height={ACTION_BUTTON_SIZE.COPY_ANNOTATION_ID} />
            </Button>}
        </ActionItem>
        {handleAnnotationEdit && <ActionItem>
            <Button size={SIZE.mini} onClick={e => {
          handleAnnotationEdit(e);
        }} shape={SHAPE.circle} kind={BTN_KIND.tertiary}>
              <Pencil title={ACTION_TYPE.EDIT_ANNOTATION} width={ACTION_BUTTON_SIZE.EDIT_ANNOTATION} height={ACTION_BUTTON_SIZE.EDIT_ANNOTATION} />
            </Button>
          </ActionItem>}
        {onAnnotationLockToggle && <ActionItem>
            <LockButton isLocked={Boolean(annotation.locked)} onLockClick={e => {
          handleToggleAnnotationLock(e, false);
        }} onUnlockClick={e => {
          handleToggleAnnotationLock(e, true);
        }} isDisabled={config?.disableHide} attributes={{
          'data-testid': LOCK_BUTTON_SELECTOR_ATTRIBUTE_ID
        }} />
          </ActionItem>}
        <ActionItem>
          <HideButton isHidden={Boolean(annotation.hidden)} onHideClick={e => {
          handleToggleAnnotationVisibility(e, true);
        }} onShowClick={e => {
          handleToggleAnnotationVisibility(e, false);
        }} isDisabled={config?.disableHide} attributes={{
          'data-selector-id': SHOW_HIDE_BUTTON_SELECTOR_ATTRIBUTE_ID
        }} />
        </ActionItem>
        {handleAnnotationCheckedToggle && <ActionItem>
            <Checkbox disabled={isCheckboxDisabled} checked={isChecked} onChange={e => handleAnnotationCheckedToggle(e)} />
          </ActionItem>}
        {handleAnnotationDelete && <ActionItem>
            <Button size={SIZE.mini} onClick={e => {
          handleAnnotationDelete(e);
        }} shape={SHAPE.circle} kind={BTN_KIND.tertiary} overrides={deleteButtonOverride} disabled={config?.disableDelete ?? false} data-selector-id={DELETE_BUTTON_SELECTOR_ATTRIBUTE_ID} data-e2e-id={`delete-${id}`}>
              <TrashCan title={ACTION_TYPE.DELETE_ANNOTATION} size={ACTION_BUTTON_SIZE.DELETE_ANNOTATION} />
            </Button>
          </ActionItem>}
      </AnnotationDetailsContainer>
    </ItemContainer>;
};
export default AnnotationSubItem;